import { useEffect, useRef, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import {
  Card,
  CardBody,
  CardTitle,
  Form,
  FormGroup,
  Label,
  Input,
  Button,
  Alert,
} from 'reactstrap';
import { useApiClient } from '../api/client.jsx';
import { useAuth } from '../auth/AuthProvider';
import { isApiError } from '../api/errors';
import { extractTokens } from '../auth/extractTokens';
import { createDebugLogger, createErrorLogger } from '../utils/debug';

const dlog = createDebugLogger('mfa-verify');
const elog = createErrorLogger('mfa-verify');

export function MfaChallengePage() {
  const { verifyMfa } = useApiClient();
  const auth = useAuth();
  const navigate = useNavigate();
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const codeRef = useRef(null);

  useEffect(() => {
    codeRef.current?.focus();
  }, []);

  // Wenn MFA gerade erfolgreich verifiziert wurde, ist status === 'authenticated'
  // und mfaChallenge === null. In dem Fall NICHT zu /login redirecten —
  // RootRedirect übernimmt von / aus die korrekte Weiterleitung zum Dashboard.
  if (auth.status === 'authenticated') {
    return <Navigate to="/" replace />;
  }
  if (auth.status !== 'mfa-pending' || !auth.mfaChallenge) {
    return <Navigate to="/login" replace />;
  }

  async function onSubmit(e) {
    e.preventDefault();
    if (!auth.mfaChallenge) return;
    setBusy(true);
    setError(null);
    try {
      const res = await verifyMfa(auth.mfaChallenge, code);
      dlog('response', res);
      if (res?.mfaRequired) {
        setError('MFA noch nicht abgeschlossen');
        return;
      }
      const tokens = extractTokens(res, 'mfa-verify-response');
      auth.setTokens(tokens);
      navigate('/', { replace: true });
    } catch (err) {
      elog('error', err);
      setError(isApiError(err) ? err.message : (err?.message ?? 'Code ungültig'));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="d-flex align-items-center justify-content-center vh-100 bg-body-tertiary">
      <Card style={{ width: 340 }}>
        <CardBody>
          <CardTitle tag="h5" className="mb-3">
            🔐 Zwei-Faktor-Code
          </CardTitle>
          <p className="text-muted small">
            6-stelliger Code aus deiner Authenticator-App.
          </p>
          <Form onSubmit={onSubmit}>
            <FormGroup>
              <Label for="mfaCode" className="visually-hidden">
                MFA-Code
              </Label>
              <Input
                innerRef={codeRef}
                id="mfaCode"
                inputMode="numeric"
                pattern="[0-9]{6}"
                maxLength={6}
                value={code}
                onChange={(e) => setCode(e.target.value.replace(/\D/g, ''))}
                style={{
                  fontSize: 22,
                  letterSpacing: 6,
                  textAlign: 'center',
                  fontFamily: 'ui-monospace, monospace',
                }}
              />
            </FormGroup>
            {error && <Alert color="danger">{error}</Alert>}
            <Button color="primary" type="submit" block disabled={busy || code.length !== 6}>
              {busy ? 'Prüfen…' : 'Bestätigen'}
            </Button>
          </Form>
        </CardBody>
      </Card>
    </div>
  );
}
